import { useState, useMemo, useRef, useEffect } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import {
  Search, Check, X, HeartPulse, Wrench, Home, Sparkles,
  UtensilsCrossed, GraduationCap, Scale, Camera, Dumbbell,
  Monitor, LayoutGrid, CircleEllipsis, Briefcase, PartyPopper, Church,
  Landmark, ShoppingBag, Car, Handshake
} from "lucide-react";

const CATEGORY_ICONS = {
  "gesundheit": HeartPulse,
  "handwerk": Wrench,
  "gebaeude": Home,
  "beauty": Sparkles,
  "gastro": UtensilsCrossed,
  "bildung": GraduationCap,
  "beratung": Briefcase,
  "recht": Scale,
  "it": Monitor,
  "kreativ": Camera,
  "events": PartyPopper,
  "religion": Church,
  "institutionen": Landmark,
  "shop": ShoppingBag,
  "fitness": Dumbbell,
  "mobilitaet": Car,
  "dienste": Handshake,
};

const CATEGORY_COLORS = {
  "gesundheit": { pill: "bg-rose-50 text-rose-700 border-rose-200", active: "bg-rose-600 text-white border-rose-600", dot: "bg-rose-500" },
  "handwerk": { pill: "bg-orange-50 text-orange-700 border-orange-200", active: "bg-orange-600 text-white border-orange-600", dot: "bg-orange-500" },
  "gebaeude": { pill: "bg-emerald-50 text-emerald-700 border-emerald-200", active: "bg-emerald-600 text-white border-emerald-600", dot: "bg-emerald-500" },
  "beauty": { pill: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", active: "bg-fuchsia-600 text-white border-fuchsia-600", dot: "bg-fuchsia-500" },
  "gastro": { pill: "bg-amber-50 text-amber-700 border-amber-200", active: "bg-amber-600 text-white border-amber-600", dot: "bg-amber-500" },
  "bildung": { pill: "bg-indigo-50 text-indigo-700 border-indigo-200", active: "bg-indigo-600 text-white border-indigo-600", dot: "bg-indigo-500" },
  "beratung": { pill: "bg-blue-50 text-blue-700 border-blue-200", active: "bg-blue-600 text-white border-blue-600", dot: "bg-blue-500" },
  "recht": { pill: "bg-slate-50 text-slate-700 border-slate-200", active: "bg-slate-700 text-white border-slate-700", dot: "bg-slate-600" },
  "it": { pill: "bg-sky-50 text-sky-700 border-sky-200", active: "bg-sky-600 text-white border-sky-600", dot: "bg-sky-500" },
  "kreativ": { pill: "bg-pink-50 text-pink-700 border-pink-200", active: "bg-pink-600 text-white border-pink-600", dot: "bg-pink-500" },
  "events": { pill: "bg-purple-50 text-purple-700 border-purple-200", active: "bg-purple-600 text-white border-purple-600", dot: "bg-purple-500" },
  "religion": { pill: "bg-yellow-50 text-yellow-800 border-yellow-200", active: "bg-yellow-600 text-white border-yellow-600", dot: "bg-yellow-500" },
  "institutionen": { pill: "bg-teal-50 text-teal-700 border-teal-200", active: "bg-teal-600 text-white border-teal-600", dot: "bg-teal-500" },
  "shop": { pill: "bg-lime-50 text-lime-800 border-lime-200", active: "bg-lime-600 text-white border-lime-600", dot: "bg-lime-500" },
  "fitness": { pill: "bg-cyan-50 text-cyan-700 border-cyan-200", active: "bg-cyan-600 text-white border-cyan-600", dot: "bg-cyan-500" },
  "mobilitaet": { pill: "bg-red-50 text-red-700 border-red-200", active: "bg-red-600 text-white border-red-600", dot: "bg-red-500" },
  "dienste": { pill: "bg-violet-50 text-violet-700 border-violet-200", active: "bg-violet-600 text-white border-violet-600", dot: "bg-violet-500" },
};

export function IndustryPicker({ open, onOpenChange, industries, categories = [], selected, onSelect }) {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setActiveCategory(null);
    }
  }, [open]);

  const isSearching = search.trim().length > 0;

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const q = search.toLowerCase();
    return (industries || []).filter((i) => i.toLowerCase().includes(q));
  }, [industries, search, isSearching]);

  const activeItems = useMemo(() => {
    if (!activeCategory) return [];
    const cat = categories.find(c => c.id === activeCategory);
    return cat ? cat.industries : [];
  }, [activeCategory, categories]);

  const handleSelect = (industry) => {
    onSelect(industry);
    setSearch("");
  };

  const findCategoryForIndustry = (name) => {
    for (const cat of categories) {
      if (cat.industries.includes(name)) return cat.id;
    }
    return null;
  };

  const renderIndustryItem = (industry) => {
    const isSelected = selected === industry;
    const catId = findCategoryForIndustry(industry);
    const colors = CATEGORY_COLORS[catId];
    return (
      <button
        key={industry}
        data-testid={`industry-option-${industry.toLowerCase().replace(/[\s/]+/g, "-")}`}
        onClick={() => handleSelect(industry)}
        className={`group relative flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-left transition-all duration-200 ${
          isSelected
            ? "bg-indigo-600 shadow-lg shadow-indigo-200/60 ring-2 ring-indigo-600"
            : "bg-white border border-slate-200/80 hover:border-indigo-300 hover:shadow-sm"
        }`}
      >
        {colors && !isSelected && (
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
        )}
        {isSelected && (
          <Check className="w-4 h-4 text-white flex-shrink-0" />
        )}
        <span className={`text-sm font-medium leading-tight truncate ${
          isSelected ? "text-white" : "text-slate-700 group-hover:text-slate-900"
        }`}>
          {industry}
        </span>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[540px] max-h-[85vh] p-0 gap-0 overflow-hidden rounded-2xl border-0 shadow-2xl [&>button:last-child]:hidden"
        data-testid="industry-picker-dialog"
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900" data-testid="industry-picker-title">
                Branche wählen
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">Wähle eine Kategorie oder suche direkt</p>
            </div>
            <button
              data-testid="industry-picker-close"
              onClick={() => onOpenChange(false)}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="industry-search-input"
              type="text"
              placeholder="z.B. Zahnarzt, Maler, Coach..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50/50 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 focus:bg-white transition-all"
            />
          </div>
        </div>

        {/* Content area */}
        <div className="overflow-y-auto max-h-[58vh]" ref={scrollRef}>
          {isSearching ? (
            /* Search Results */
            <div className="px-5 pb-5 pt-1">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2.5">
                {searchResults.length} Ergebnis{searchResults.length !== 1 ? "se" : ""}
              </p>
              {searchResults.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm" data-testid="industry-no-results">
                  Keine Branche gefunden
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {searchResults.map(renderIndustryItem)}
                </div>
              )}
            </div>
          ) : activeCategory ? (
            /* Category Detail View */
            <div className="px-5 pb-5 pt-1">
              <button
                data-testid="industry-back-to-categories"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-1.5 text-sm text-indigo-600 font-medium mb-3 hover:text-indigo-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Alle Kategorien
              </button>
              {(() => {
                const cat = categories.find(c => c.id === activeCategory);
                if (!cat) return null;
                const CatIcon = CATEGORY_ICONS[cat.id] || LayoutGrid;
                const colors = CATEGORY_COLORS[cat.id];
                return (
                  <>
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors?.active || "bg-indigo-600 text-white"}`}>
                        <CatIcon className="w-4 h-4" />
                      </div>
                      <h3 className="text-base font-semibold text-slate-900">{cat.name}</h3>
                      <span className="text-xs text-slate-400 ml-auto">{cat.industries.length} Branchen</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {activeItems.map(renderIndustryItem)}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            /* Category Grid */
            <div className="px-5 pb-5 pt-1">
              {selected && (
                <div className="mb-4 p-3 rounded-xl bg-indigo-50/70 border border-indigo-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-900">Ausgewählt: {selected}</span>
                  </div>
                  <button
                    data-testid="industry-clear-selection"
                    onClick={() => onSelect("")}
                    className="text-xs text-indigo-500 hover:text-indigo-700 font-medium transition-colors"
                  >
                    Ändern
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2.5">
                {categories.map((cat) => {
                  const CatIcon = CATEGORY_ICONS[cat.id] || LayoutGrid;
                  const colors = CATEGORY_COLORS[cat.id];
                  const hasSelected = cat.industries.includes(selected);
                  return (
                    <button
                      key={cat.id}
                      data-testid={`industry-category-${cat.id}`}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`group relative flex flex-col items-start p-4 rounded-xl border transition-all duration-200 text-left ${
                        hasSelected
                          ? `${colors?.pill || "bg-indigo-50 border-indigo-200"} ring-2 ring-indigo-500/30`
                          : "bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-sm"
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2.5 transition-colors ${
                        hasSelected
                          ? (colors?.active || "bg-indigo-600 text-white")
                          : (colors?.pill || "bg-slate-100 text-slate-600")
                      }`}>
                        <CatIcon className="w-4.5 h-4.5" />
                      </div>
                      <span className="text-sm font-semibold text-slate-800 leading-tight">{cat.name}</span>
                      <span className="text-xs text-slate-400 mt-0.5">{cat.industries.length} Branchen</span>
                      {hasSelected && (
                        <div className="absolute top-2.5 right-2.5">
                          <Check className="w-4 h-4 text-indigo-600" />
                        </div>
                      )}
                    </button>
                  );
                })}
                {/* Andere Option */}
                <button
                  data-testid="industry-option-andere"
                  onClick={() => handleSelect("Andere")}
                  className={`group relative flex flex-col items-start p-4 rounded-xl border transition-all duration-200 text-left ${
                    selected === "Andere"
                      ? "bg-gray-50 border-gray-300 ring-2 ring-indigo-500/30"
                      : "bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-sm"
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2.5 ${
                    selected === "Andere" ? "bg-gray-600 text-white" : "bg-gray-100 text-gray-500"
                  }`}>
                    <CircleEllipsis className="w-4.5 h-4.5" />
                  </div>
                  <span className="text-sm font-semibold text-slate-800">Andere Branche</span>
                  <span className="text-xs text-slate-400 mt-0.5">Nicht aufgelistet</span>
                  {selected === "Andere" && (
                    <div className="absolute top-2.5 right-2.5">
                      <Check className="w-4 h-4 text-indigo-600" />
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
